import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Descriptions, Empty, Space, Spin, Table, Tabs, Tag } from 'antd';
import dayjs from 'dayjs';
import { getDebtDetail } from '../../services/debt';
import { CreditorTypeOptions, EvidenceTypeOptions, labelOfAuditStatus, labelOfCollectionStatus, labelOfDebtStatus, labelOfDebtType, labelOfLowPriceOption, labelOfDisposalMethod } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';

/**
 * 债权详情通用展示组件
 * 目标：统一各页面的债权详情展示逻辑，避免重复实现与维护成本。
 * 性能：债务人、证据等可能存在大量数据，采用 Table 分页展示，避免一次性渲染过多 DOM。
 */
export const DebtDetailView = ({ debtId }) => {
  const { isMobile } = useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const id = debtId ? String(debtId) : '';
    if (!id) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await getDebtDetail(id);
        if (!cancelled) {
          setDetail(data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debtId]);

  const evidenceTypeLabelByCode = useMemo(() => {
    const map = new Map();
    (Array.isArray(EvidenceTypeOptions) ? EvidenceTypeOptions : []).forEach((o) => {
      map.set(String(o.code), o.label);
    });
    return map;
  }, []);

  const debt = detail?.debt || null;
  const creditor = detail?.creditor || null;
  const debtorsPersonal = Array.isArray(detail?.debtorsPersonal) ? detail.debtorsPersonal : [];
  const debtorsEnterprise = Array.isArray(detail?.debtorsEnterprise) ? detail.debtorsEnterprise : [];
  const evidences = Array.isArray(detail?.evidences) ? detail.evidences : [];

  const creditorTypeLabel = useMemo(() => {
    const v = creditor?.creditorType;
    const found = (Array.isArray(CreditorTypeOptions) ? CreditorTypeOptions : []).find(o => String(o.value) === String(v));
    return found ? found.label : (v ?? '');
  }, [creditor?.creditorType]);

  const debtorPersonalColumns = useMemo(() => ([
    { title: '姓名', dataIndex: 'name', key: 'name', width: 120, ellipsis: true },
    { title: '证件号', dataIndex: 'idNo', key: 'idNo', width: 180, ellipsis: true },
    { title: '电话', dataIndex: 'phone', key: 'phone', width: 140, ellipsis: true },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 180, ellipsis: true },
    { title: '省', dataIndex: 'province', key: 'province', width: 100, ellipsis: true },
    { title: '市', dataIndex: 'city', key: 'city', width: 120, ellipsis: true },
    { title: '详细地址', dataIndex: 'address', key: 'address', ellipsis: true },
  ]), []);

  const debtorEnterpriseColumns = useMemo(() => ([
    { title: '企业名称', dataIndex: 'companyName', key: 'companyName', width: 220, ellipsis: true },
    { title: '统一社会信用代码', dataIndex: 'socialCreditCode', key: 'socialCreditCode', width: 200, ellipsis: true },
    { title: '法定代表人姓名', dataIndex: 'legalPersonName', key: 'legalPersonName', width: 140, ellipsis: true },
    { title: '法定代表人身份证号', dataIndex: 'legalPersonIdNo', key: 'legalPersonIdNo', width: 180, ellipsis: true },
    { title: '电话', dataIndex: 'phone', key: 'phone', width: 140, ellipsis: true },
    { title: '邮箱', dataIndex: 'email', key: 'email', width: 180, ellipsis: true },
    { title: '省', dataIndex: 'province', key: 'province', width: 100, ellipsis: true },
    { title: '市', dataIndex: 'city', key: 'city', width: 120, ellipsis: true },
    { title: '详细地址', dataIndex: 'address', key: 'address', ellipsis: true },
  ]), []);

  const evidenceColumns = useMemo(() => ([
    {
      title: '类型',
      dataIndex: 'evidenceType',
      key: 'evidenceType',
      width: 160,
      render: (v) => {
        const code = v == null ? '' : String(v);
        const label = evidenceTypeLabelByCode.get(code);
        return label ? `${code}-${label}` : code;
      },
    },
    {
      title: '文件名称',
      dataIndex: 'fileUrl',
      key: 'fileUrl',
      ellipsis: true,
      render: (v) => {
        const url = v ? String(v) : '';
        const name = url ? url.split('/').pop() : '';
        return name || '-';
      },
    },
    { title: '备注', dataIndex: 'remark', key: 'remark', width: 240, ellipsis: true },
    {
      title: '操作',
      key: 'action',
      width: 90,
      render: (_, r) => {
        const url = r?.fileUrl ? String(r.fileUrl) : '';
        if (!url) return '-';
        return (
          <a href={url} target="_blank" rel="noreferrer">
            查看
          </a>
        );
      },
    },
  ]), [evidenceTypeLabelByCode]);

  return (
    <Spin spinning={loading}>
      {!debt ? (
        <Empty description={debtId ? '暂无债权详情数据' : '请选择债权'} />
      ) : (
        <div>
          <Descriptions title="基本信息" bordered size="small" column={isMobile ? 1 : 2}>
            <Descriptions.Item label="编号">{debt?.debtNo || '-'}</Descriptions.Item>
            <Descriptions.Item label="债权名称">{debt?.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="债权类型">{labelOfDebtType(debt?.debtType)}</Descriptions.Item>
            <Descriptions.Item label="发生日">{debt?.debtOccurrenceDate ? dayjs(debt.debtOccurrenceDate).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
            <Descriptions.Item label="本金金额">{formatAmountText(debt?.principalAmount)}</Descriptions.Item>
            <Descriptions.Item label="利息金额">{formatAmountText(debt?.interestAmount)}</Descriptions.Item>
            <Descriptions.Item label="违约金">{formatAmountText(debt?.penaltyAmount)}</Descriptions.Item>
            <Descriptions.Item label="其他费用">{formatAmountText(debt?.otherAmount)}</Descriptions.Item>
            <Descriptions.Item label="合计金额">{formatAmountText(debt?.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="债权说明" span={isMobile ? 1 : 2}>{debt?.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="匿名">{(debt?.isAnonymous ?? 0) === 1 ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="原始债权人">{(debt?.isOriginalCreditor ?? 0) === 1 ? '是' : '否'}</Descriptions.Item>
            <Descriptions.Item label="处置方式">{labelOfDisposalMethod(debt?.disposalMethod)}</Descriptions.Item>
            {String(debt?.disposalMethod) === 'LOW_PRICE_TRANSFER' && (
              <Descriptions.Item label="低价转让方式">{labelOfLowPriceOption(debt?.lowPriceOption)}</Descriptions.Item>
            )}
            {String(debt?.disposalMethod) === 'LOW_PRICE_TRANSFER' && String(debt?.lowPriceOption) === 'PRICING' && (
              <Descriptions.Item label="定价金额">{formatAmountText(debt?.transferPricingAmount)}</Descriptions.Item>
            )}
            {String(debt?.disposalMethod) === 'LOW_PRICE_TRANSFER' && String(debt?.lowPriceOption) === 'DISCOUNT' && (
              <Descriptions.Item label="折扣比例">{debt?.transferDiscountRate != null ? `${debt.transferDiscountRate}%` : '-'}</Descriptions.Item>
            )}
            <Descriptions.Item label="审核状态">
              <Tag>{labelOfAuditStatus(debt?.auditStatus)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="债权状态">
              <Tag>{labelOfDebtStatus(debt?.status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="催收状态">
              <Tag>{labelOfCollectionStatus(debt?.collectionStatus)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="驳回原因" span={isMobile ? 1 : 2}>{debt?.rejectReason || '-'}</Descriptions.Item>
          </Descriptions>

          {creditor && (
            <Descriptions title="债权人" bordered size="small" column={isMobile ? 1 : 2} style={{ marginTop: 16 }}>
              <Descriptions.Item label="债权人类型">{creditorTypeLabel || '-'}</Descriptions.Item>
              {String(creditor?.creditorType) === 'P' ? (
                <>
                  <Descriptions.Item label="姓名">{creditor?.name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="身份证号">{creditor?.idCardNo || '-'}</Descriptions.Item>
                </>
              ) : (
                <>
                  <Descriptions.Item label="企业名称">{creditor?.companyName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="统一社会信用代码">{creditor?.socialCreditCode || '-'}</Descriptions.Item>
                  <Descriptions.Item label="法定代表人姓名">{creditor?.legalPersonName || '-'}</Descriptions.Item>
                  <Descriptions.Item label="法定代表人身份证号">{creditor?.legalPersonIdNo || '-'}</Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="电话">{creditor?.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="邮箱">{creditor?.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="省">{creditor?.province || '-'}</Descriptions.Item>
              <Descriptions.Item label="市">{creditor?.city || '-'}</Descriptions.Item>
              <Descriptions.Item label="详细地址" span={isMobile ? 1 : 2}>{creditor?.address || '-'}</Descriptions.Item>
            </Descriptions>
          )}

          <Tabs
            style={{ marginTop: 16 }}
            size={isMobile ? 'small' : 'middle'}
            items={[
              {
                key: 'debtors',
                label: '债务人',
                children: (
                  <Tabs
                    size={isMobile ? 'small' : 'middle'}
                    items={[
                      {
                        key: 'personal',
                        label: `个人(${debtorsPersonal.length})`,
                        children: (
                          <Table
                            size="small"
                            rowKey={(r, i) => r?.id ?? `p-${i}`}
                            columns={debtorPersonalColumns}
                            dataSource={debtorsPersonal}
                            pagination={{
                              pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                              showSizeChanger: !isMobile && PAGINATION_ENABLE_SIZE_CHANGER,
                              pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                            }}
                            scroll={{ x: 'max-content' }}
                          />
                        ),
                      },
                      {
                        key: 'enterprise',
                        label: `企业(${debtorsEnterprise.length})`,
                        children: (
                          <Table
                            size="small"
                            rowKey={(r, i) => r?.id ?? `e-${i}`}
                            columns={debtorEnterpriseColumns}
                            dataSource={debtorsEnterprise}
                            pagination={{
                              pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                              showSizeChanger: !isMobile && PAGINATION_ENABLE_SIZE_CHANGER,
                              pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                            }}
                            scroll={{ x: 'max-content' }}
                          />
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'evidences',
                label: `证据材料(${evidences.length})`,
                children: (
                  <Table
                    size="small"
                    rowKey={(r, i) => r?.id ?? `ev-${i}`}
                    columns={evidenceColumns}
                    dataSource={evidences}
                    pagination={{
                      pageSize: PAGINATION_DEFAULT_PAGE_SIZE,
                      showSizeChanger: !isMobile && PAGINATION_ENABLE_SIZE_CHANGER,
                      pageSizeOptions: PAGINATION_PAGE_SIZE_OPTIONS,
                    }}
                    scroll={{ x: 'max-content' }}
                  />
                ),
              },
            ]}
          />
        </div>
      )}
    </Spin>
  );
};

/**
 * 债权详情页（公用）
 * 用途：作为统一入口页面，供列表/历史/跟进等场景跳转查看债权完整信息。
 */
const DebtDetailPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { id } = useParams();

  return (
    <div className="page-container p-debt-management-debt-detail">
      <div className="page-content space-y-md">
        <div className="page-title">债权详情</div>
        <Card className="card-compact">
          <Space wrap style={{ marginBottom: 12, width: isMobile ? '100%' : undefined }}>
            <Button onClick={() => navigate(-1)} block={isMobile}>返回</Button>
          </Space>
          <DebtDetailView debtId={id} />
        </Card>
      </div>
    </div>
  );
};

export default DebtDetailPage;
